/*
 * sk-selleasy-variant-sync.js
 *
 * 目的: Selleasy の Frequently Bought Together / Product add-ons ウィジェットの
 *       アンカー（1品目＝表示中の商品）が、買い場で選択中のバリアントに追従しない問題を
 *       テーマ側で吸収する。
 *
 * 背景（2026-08-09 実測）:
 *   買い場のラジオと購入フォームの input[name=id] は正しく切り替わるのに、
 *   Selleasy のアンカー select だけ既定バリアントのまま残る。結果、Combo を選んだ客が
 *   「一緒にカートに追加」を押すと単体が入る（誤購入＋粗利毀損）。
 *   A1 mini / H2D / X2D で再現。Selleasy 側に制御設定は存在しない
 *   （オファー設定・FBTウィジェット設定・Common settings の3面を管理画面で実見）。
 *
 * 設計（dev-dojo critical レビューの指摘を反映・2026-08-09）:
 *   1. トリガーはテーマ純正の `product:variant-change`（assets/scripts.js:1006）。
 *      setProductInfo / updateOptionValues の後に発火するので「再描画待ちの推測タイマー」が不要。
 *      兄弟スクリプト sk-mobile-cro.js:170 / sk-p2s-buybox-pilot.js:125 と同じ作法。
 *   2. ウィジェットは遅延描画される。バリアント変化を待つだけでは `?variant=` 深リンク着地
 *      （広告・コレクション・本文リンクからの最頻導線）で一度も同期されない。
 *      よって「ウィジェット出現」も上限付きバックオフで検知する。
 *   3. ウィジェット特定は構造セレクタ優先（select.lb-shared-variants-select）。
 *      見出し文言は Selleasy 管理画面でオファーごとに編集できるため最終フォールバックに降格。
 *   4. アンカー select は「option値が JSON で isForProduct を持つもの」を全 select から探す。
 *      先頭が必ずアンカーという前提を置かない。
 *   5. 成否を window.__skSelleasySync に残す（無音で死なせない）。
 *
 * 停止方法（テーマ再デプロイ不要）:
 *   shop metafield `skhonpo.selleasy_sync` に "off" を set すると layout 側で読み込まれなくなる。
 *   未設定時は有効（既定ON）。
 *
 * 依存している外部DOM契約（Selleasy・2026-08-09 検証。拡張バージョン lb-upsell-431）:
 *   - FBT コンテナ: `.selleasy-block-fbt`（サーバー描画・A1 mini / H2D / X2D / P2S で実在確認）
 *   - アンカー select: `select.lb-shared-variants-select`
 *     ※ assets/base-customize.css:575 が既に本番スタイルでこのセレクタを使用
 *   - option の value: JSON 文字列 `{"isForProduct":true,"id":<variantId>,...}`
 *   - 見出し（最終フォールバック）: 「よく一緒に購入されています」/「一緒にご購入がおすすめ」
 *     ※ 後者は FBT 側の見出し（addon 側 `.selleasy-block-product-addon` とは別・実測確認済み）
 *   いずれかが変わったら同期は止まる。その場合 window.__skSelleasySync.reason に理由が残る。
 *
 * fail-stale への対処（2026-08-09 Sho裁定「A案」で実装）:
 *   売切バリアント・オファーで限定されたバリアントは Selleasy 側が option を持たない
 *   （lb-upsell.js の filterProducts が available でないバリアントを除外する。
 *    `isSoldOutProductsShown` という設定は実在するが
 *    `type === "product-page-addon-widget"` にハードコードで限定されており FBT には効かない。
 *    2026-08-09 に lb-upsell-431 のバンドルを直読みして確認済み）。
 *   その場合 hit=-1 となり、アンカーは「直前に選ばれていた別バリアント」のまま残る。
 *   放置すると表示と違うバリアントが投入される＝本スクリプトが潰そうとしている事象が残るので、
 *   一致しない間は FBT の「一緒にカートに追加」を無効化する。
 *
 *   ⚠ 無効化の要は **capture フェーズでのクリック遮断**であって見た目ではない。
 *   Selleasy がウィジェットを再描画すると付与したクラスは失われるが、クリックのたびに
 *   sync() を走らせてから遮断を判定するため、見た目が古くてもクリックは必ず守られる。
 *   CTA は <button> ではなく div（.lb-upsell-tot-btn）なので disabled 属性は効かない。
 */
(function () {
  'use strict';

  var TITLES = /よく一緒に購入されています|一緒にご購入がおすすめ/;
  var BACKOFF = [300, 700, 1500, 3000, 5000];   // ウィジェット出現待ち（合計約10.5秒で諦める）
  var CTA = '.lb-upsell-tot-btn';               // FBTの「一緒にカートに追加」（div・2026-08-09 実測）
  var NOTE_MSG = '選択中のオプションはセットに追加できません';
  var backoffIdx = 0;
  var rafPending = false;
  var blocked = false;

  window.__skSelleasySync = { ok: false, reason: 'init', syncs: 0, at: null, blocked: false };

  function mark(ok, reason) {
    var s = window.__skSelleasySync;
    s.ok = ok;
    s.reason = reason;
    s.at = Date.now();
    try { document.documentElement.dataset.skFbtSync = reason; } catch (e) {}
  }

  /* 一度だけスタイルを挿す。kill switch で本スクリプトごと読み込まれなければ何も起きない。 */
  function ensureStyle() {
    if (document.getElementById('sk-fbt-blocked-style')) return;
    var s = document.createElement('style');
    s.id = 'sk-fbt-blocked-style';
    s.textContent =
      '.sk-fbt-blocked{opacity:.45;cursor:not-allowed !important;pointer-events:none;}' +
      '.sk-fbt-blocked-note{margin:8px 0 0;font-size:12px;line-height:1.6;color:#5f6368;text-align:center;}';
    document.head.appendChild(s);
  }

  /* 一致しない間は FBT の CTA を無効化する。見た目はあくまで補助で、
     実際の防御は下の capture フェーズのクリック遮断が担う。 */
  function setBlocked(on) {
    blocked = !!on;
    window.__skSelleasySync.blocked = blocked;
    var fbt = document.querySelector('.selleasy-block-fbt');
    if (!fbt) return;
    var cta = fbt.querySelector(CTA);
    if (!cta) return;

    if (blocked) {
      ensureStyle();
      cta.classList.add('sk-fbt-blocked');
      cta.setAttribute('aria-disabled', 'true');
      if (!fbt.querySelector('.sk-fbt-blocked-note')) {
        var p = document.createElement('p');
        p.className = 'sk-fbt-blocked-note';
        p.textContent = NOTE_MSG;
        cta.parentNode.insertBefore(p, cta.nextSibling);
      }
    } else {
      cta.classList.remove('sk-fbt-blocked');
      cta.removeAttribute('aria-disabled');
      var note = fbt.querySelector('.sk-fbt-blocked-note');
      if (note) note.parentNode.removeChild(note);
    }
  }

  /* アンカー select を返す。安定フック → 構造セレクタ → 見出し の順に降格。 */
  function anchorSelect() {
    // 1) Selleasy がサーバー描画する安定フック（2026-08-09 実測: A1 mini / H2D / X2D / P2S に存在）
    var fbt = document.querySelector('.selleasy-block-fbt');
    var hit = fbt ? pickAnchor(fbt.querySelectorAll('select')) : null;
    if (hit) return hit;

    // 2) 構造セレクタ（base-customize.css:575 が本番スタイルで使用中）
    hit = pickAnchor(document.querySelectorAll('select.lb-shared-variants-select'));
    if (hit) return hit;

    // 3) 最終フォールバック: 見出しで app block を絞ってから全 select を走査
    //    textContent を使う（innerText は同期レイアウトを強制し、display:none 配下で空を返す）
    var blocks = document.querySelectorAll('[class*="shopify-app-block"]');
    for (var i = 0; i < blocks.length; i++) {
      if (!TITLES.test(blocks[i].textContent || '')) continue;
      hit = pickAnchor(blocks[i].querySelectorAll('select'));
      if (hit) return hit;
    }
    return null;
  }

  /* option の value が JSON で isForProduct を持つ select だけをアンカーと見なす */
  function pickAnchor(selects) {
    for (var i = 0; i < selects.length; i++) {
      var opts = selects[i].options;
      for (var j = 0; j < opts.length; j++) {
        try {
          var v = JSON.parse(opts[j].value);
          if (v && v.isForProduct) return selects[i];
        } catch (e) { /* JSON でない option は無視 */ }
      }
    }
    return null;
  }

  /* 購入フォームの現在バリアント。product-form を優先（sk-subscription-selector と同じ作法） */
  function currentVariantId() {
    var f = document.querySelector('product-form form[action*="/cart/add"]')
         || document.querySelector('form[action*="/cart/add"]');
    if (!f) return null;
    var input = f.querySelector('input[name="id"]');
    return input && input.value ? input.value : null;
  }

  function sync() {
    var sel = anchorSelect();
    if (!sel) { mark(false, 'no-widget'); return false; }

    var want = currentVariantId();
    if (!want) { mark(false, 'no-variant'); return true; }   // ウィジェットは在るので探索は終了

    var hit = -1;
    for (var i = 0; i < sel.options.length; i++) {
      try {
        var v = JSON.parse(sel.options[i].value);
        if (v && v.isForProduct && String(v.id) === String(want)) { hit = i; break; }
      } catch (e) {}
    }
    if (hit < 0) { setBlocked(true); mark(false, 'no-match'); return true; }

    setBlocked(false);
    if (sel.selectedIndex === hit) { mark(true, 'already'); return true; }

    sel.selectedIndex = hit;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    window.__skSelleasySync.syncs++;
    mark(true, 'synced');
    return true;
  }

  /* 描画直後の多重呼び出しを1フレームに畳む */
  function syncCoalesced() {
    if (rafPending) return;
    rafPending = true;
    var run = function () { rafPending = false; sync(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  /* ウィジェット出現待ち。見つかるまで上限付きバックオフ。 */
  function waitForWidget() {
    if (sync()) return;                      // 出現済み（同期済み or 明示的な失敗理由あり）
    if (backoffIdx >= BACKOFF.length) return; // 諦める（reason は no-widget のまま残る）
    setTimeout(waitForWidget, BACKOFF[backoffIdx++]);
  }

  /* 1) テーマ純正イベント: バリアント変更・結合リスティングの商品スワップ両方で発火 */
  document.addEventListener('product:variant-change', function () {
    backoffIdx = 0;          // スワップでウィジェットが再マウントされる場合に備えて再探索を許す
    syncCoalesced();
    setTimeout(waitForWidget, 300);
  });

  /* 2) 「一緒にカートに追加」直前の取りこぼしを塞ぐ（capture で同期実行）
        バリアント切替から数百ms以内にボタンを押されても旧バリアントを送らせない。
        あわせて、一致しない状態でCTAが押されたらここで確実に止める。
        Selleasy の再描画で見た目のクラスが失われていても、押した瞬間に sync() を
        走らせてから判定するのでクリックは必ず守られる。 */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('[class*="lb-upsell"], [class*="lb-shared"]')) return;

    sync();

    if (blocked && t.closest(CTA)) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      setBlocked(true);   // 再描画でクラスが落ちていた場合に見た目を戻す
    }
  }, true);

  /* 3) 初回。着地時点で既に正しいバリアントの場合（?variant= 深リンク）はイベントが来ないため必須 */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWidget);
  } else {
    waitForWidget();
  }
})();
